import type { ContextSourceReceipt } from "./contextAssemblyReceipt.ts";

const READ_RPCS = ["get_bi_v2_publication_lane_v1", "load_noticing_snapshot_v1", "get_governed_bi_context_v1", "get_shared_bi_context_service_v1"];

export function queryResultContextSource(kind: string, result: unknown, rejected = false): ContextSourceReceipt {
  const row = result && typeof result === "object" ? result as { data?: unknown; error?: unknown; count?: number | null } : {};
  const failed = rejected || Boolean(row.error);
  const invalid = !result || typeof result !== "object" || Array.isArray(result)
    || !Object.prototype.hasOwnProperty.call(row, "error") || !Object.prototype.hasOwnProperty.call(row, "data")
    || (row.count != null && (!Number.isSafeInteger(row.count) || row.count < 0))
    || (row.data != null && typeof row.data !== "object");
  const rowCount = failed || invalid ? 0 : typeof row.count === "number" ? row.count : Array.isArray(row.data) ? row.data.length : row.data ? 1 : 0;
  return { kind, status: failed || invalid ? "unavailable" : rowCount ? "ready" : "empty", row_count: rowCount,
    reason_code: failed ? "query_failed" : invalid ? "invalid_payload" : rowCount ? "complete" : "no_rows" };
}

/** Observe executed read outcomes only. Never inspect/log query parameters or content.
 * The original thenable executes once for each original await, with its receiver intact.
 * Request writes and non-read RPCs are excluded. Coverage remains explicitly partial.
 */
export function observeContextQueries(client: unknown, observer: (source: ContextSourceReceipt) => void): unknown {
  const report = (kind: string, result: unknown, rejected = false) => {
    try { observer(queryResultContextSource(kind, result, rejected)); }
    catch { try { console.warn("[context-receipt] receipt_invalid"); } catch { /* Never change query semantics. */ } }
  };
  const query = (target: object, kind: string, state: { write: boolean }): object => {
    const execute = () => Promise.resolve(target).then(result => {
      if (!state.write) report(kind, result);
      return result;
    }, error => {
      if (!state.write) report(kind, null, true);
      throw error;
    });
    return new Proxy(target, {
      get(object, key) {
        const value = Reflect.get(object, key, object);
        if (key === "then" && typeof value === "function") return (resolve: (value: unknown) => unknown, reject: (error: unknown) => unknown) => execute().then(resolve, reject);
        if (key === "catch" && typeof value === "function") return (reject: (error: unknown) => unknown) => execute().catch(reject);
        if (key === "finally" && typeof value === "function") return (handler: () => void) => execute().finally(handler);
        if (typeof value !== "function") return value;
        return (...args: unknown[]) => {
          if (["insert", "update", "upsert", "delete"].includes(String(key))) state.write = true;
          const next = Reflect.apply(value, object, args);
          return next && typeof next === "object" ? query(next, kind, state) : next;
        };
      },
    });
  };
  return new Proxy(client as object, {
    get(target, key) {
      const value = Reflect.get(target, key, target);
      if (typeof value !== "function") return value;
      if (key !== "from" && key !== "rpc") return value.bind(target);
      return (...args: unknown[]) => {
        const result = Reflect.apply(value, target, args);
        const label = (key === "rpc" ? "rpc_" : "") + String(args[0]);
        const kind = /^[a-z][a-z0-9_]{0,63}$/.test(label) ? label : "unclassified_query";
        return result && typeof result === "object" ? query(result, kind, { write: key === "rpc" && !READ_RPCS.includes(String(args[0])) }) : result;
      };
    },
  });
}
